import { ItemListAnalyticsModel, OrderLineViewModel } from '@/@types/client-api';
import { usePostCartItemMutation } from '@/redux/api/client/cart';
import { useCallback } from 'react';
import { makeToastProblemMessage } from './util';
import useToast from '../useToast';
import { AnalyticsContext } from '@/analytics/constants';
import { useLazyGetItemAnalyticsModelByItemPksQuery } from '@/redux/api/client/itemAnalyticsModel';
import { trackAddToCart } from '@/analytics/ecommerce';
import { createTransactionId } from '@/analytics/analyticsUtils';
import { evictCreditTrackerCode, setCreditTrackerCode } from '../useCreditTrackerCode';
import { useDispatch } from 'react-redux';
import { openCartPanel, setIsAddingToCart, setLastAddedItem } from '@/redux/slices/cartSlice';

export interface IAddToCartProps {
  code?: string;
  priorityCode?: string;
  analyticsCoupon?: string;
  isSTS?: boolean;
  transactionId?: string;
  redirectToCart?: boolean;
  itemIndex?: number;
}

export const useAddToCart = (
  context?: AnalyticsContext,
  creditTrackerCode?: string,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  successToastMessage?: string
) => {
  const dispatch = useDispatch();
  const [cartMutation, { isLoading, isSuccess, isError, reset }] = usePostCartItemMutation();
  const { showSuccessMessage, showFailMessage } = useToast();
  const [fetchItems] = useLazyGetItemAnalyticsModelByItemPksQuery();

  const getAnalyticsItems = useCallback(
    async (
      contentCode: string,
      transactionId?: string,
      itemIndex?: number,
      couponOverride?: string
    ) => {
      const { data } = await fetchItems({
        itemPks: [contentCode],
        populatePrice: true,
        limitPriceCalls: true
      });

      if (data && data.length > 0) {
        const itemAnalyticsModel =
          couponOverride !== undefined ? { ...data[0], coupon: couponOverride.trim() } : data[0];

        trackAddToCart(
          itemAnalyticsModel,
          context,
          transactionId,
          itemListAnalyticsModel,
          itemIndex
        );
        dispatch(setLastAddedItem(itemAnalyticsModel));
      }
    },
    [context, itemListAnalyticsModel, dispatch, fetchItems]
  );

  const addToCart = useCallback(
    async (props: IAddToCartProps) => {
      const {
        code,
        priorityCode,
        analyticsCoupon,
        isSTS,
        transactionId: providedTransactionId,
        redirectToCart,
        itemIndex
      } = props;

      if (!code) return;

      dispatch(setIsAddingToCart(true));

      let transactionId = providedTransactionId;
      if (!transactionId) transactionId = createTransactionId('WEBZERO');

      getAnalyticsItems(code, transactionId, itemIndex, analyticsCoupon);

      if (creditTrackerCode) setCreditTrackerCode(code, creditTrackerCode);

      const cartRequest: OrderLineViewModel = {
        code: code,
        quantity: 1,
        isSTS,
        priorityCode,
        itemListAnalyticsModel: itemListAnalyticsModel
      };
      const cartFailedMessage = makeToastProblemMessage('cart');

      dispatch(openCartPanel());

      const cartResponse = await cartMutation(cartRequest);
      if ('error' in cartResponse) {
        showFailMessage(cartFailedMessage);
      } else if ('data' in cartResponse) {
        evictCreditTrackerCode(code);
        if (successToastMessage) showSuccessMessage(successToastMessage);
      }

      dispatch(setIsAddingToCart(false));

      if (redirectToCart) {
        window.location.href = cartResponse?.data?.cartUrl ?? '';
      }
    },
    [
      dispatch,
      getAnalyticsItems,
      creditTrackerCode,
      cartMutation,
      showFailMessage,
      itemListAnalyticsModel,
      successToastMessage,
      showSuccessMessage
    ]
  );

  return { addToCart, isLoading, isSuccess, isError, reset };
};
